// Accordion Element
class AccordionElement extends HTMLElement {
  connectedCallback() {
    this.setAttribute('role', 'presentation');
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('accordion-toggle', (event) => this.handleToggle(event));

    if (this.hasAttribute('data-toggle-first')) {
      const firstItem = this.querySelector('accordion-item');
      if (firstItem) {
        firstItem.setAttribute('open', '');
      }
    }

    this.handleResize();
    window.addEventListener('resize', this.handleResize.bind(this));

    // Initialize sticky nav if this is the product extended details accordion
    if (this.closest('.product-extended-details')) {
      this.initStickyNav();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize.bind(this));
    if (this.stickyNav) {
      this.observer?.disconnect();
      this.stickyNav.remove();
    }
  }

  initStickyNav() {
    const accordionItems = this.querySelectorAll('accordion-item[id]');
    if (accordionItems.length < 2) return;

    // Remove existing sticky nav if it exists
    const existingNav = document.querySelector('.accordion-sticky-nav');
    if (existingNav) {
      existingNav.remove();
    }

    this.isNavigating = false;

    // Create sticky navigation
    this.createStickyNav(accordionItems);
    this.setupIntersectionObserver();
    this.setupScrollTracking();
    this.setupHeaderHeightTracking();
  }

  createStickyNav(items) {
    this.stickyNav = document.createElement('div');
    this.stickyNav.className = 'accordion-sticky-nav accordion-sticky-nav--bottom';
    this.stickyNav.setAttribute('role', 'navigation');
    this.stickyNav.setAttribute('aria-label', 'Accordion sections');
    this.stickyNav.style.display = 'block'; // Show immediately at bottom

    const navContainer = document.createElement('div');
    navContainer.className = 'accordion-sticky-nav__container page-width';

    items.forEach((item, index) => {
      const header = item.querySelector('.accordion-header');
      if (!header) return;

      const navItem = document.createElement('button');
      navItem.className = 'accordion-sticky-nav__item';
      navItem.setAttribute('type', 'button');
      navItem.setAttribute('data-target', item.id);
      navItem.textContent = header.textContent.trim();
      
      navItem.addEventListener('click', () => this.handleStickyNavClick(item));
      navItem.addEventListener('keydown', (e) => this.handleStickyNavKeydown(e, index, items.length));

      navContainer.appendChild(navItem);
    });

    this.stickyNav.appendChild(navContainer);
    document.body.appendChild(this.stickyNav);
  }

  setupIntersectionObserver() {
    const accordionContainer = this.closest('.product-extended-details');
    if (!accordionContainer) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (this.stickyNav && !this.isNavigating) {
            const rect = entry.boundingClientRect;
            const viewportHeight = window.innerHeight;
            const isMobile = window.innerWidth < 768;
            
            // Check if accordion is below viewport (not yet reached)
            const isBelowViewport = rect.top > viewportHeight;
            
            // Check if accordion is above viewport (scrolled past)
            const isAboveViewport = rect.bottom < 0;
            
            if (isMobile) {
              // Mobile: Only show at top when scrolled past header
              const headerHeight = document.querySelector('.section-header')?.offsetHeight || 0;
              const scrolledPastHeader = window.pageYOffset > headerHeight;
              
              if ((entry.isIntersecting || isAboveViewport) && scrolledPastHeader) {
                this.stickyNav.style.display = 'block';
                this.stickyNav.classList.remove('accordion-sticky-nav--bottom');
                this.stickyNav.classList.add('accordion-sticky-nav--top');
              } else {
                this.stickyNav.style.display = 'none';
              }
            } else {
              // Desktop/Tablet: Original behavior
              if (isBelowViewport) {
                this.stickyNav.style.display = 'block';
                this.stickyNav.classList.remove('accordion-sticky-nav--top');
                this.stickyNav.classList.add('accordion-sticky-nav--bottom');
              } else if (entry.isIntersecting || isAboveViewport) {
                this.stickyNav.style.display = 'block';
                this.stickyNav.classList.remove('accordion-sticky-nav--bottom');
                this.stickyNav.classList.add('accordion-sticky-nav--top');
              } else {
                this.stickyNav.style.display = 'none';
              }
            }
          }
        });
      },
      { 
        threshold: [0, 0.1],
        rootMargin: '0px 0px 0px 0px' 
      }
    );

    this.observer.observe(accordionContainer);
  }

  setupScrollTracking() {
    const accordionItems = this.querySelectorAll('accordion-item[id]');
    
    const itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            this.updateActiveNavItem(entry.target.id);
          }
        });
      },
      { threshold: [0, 0.5, 1], rootMargin: '-20% 0px -20% 0px' }
    );

    accordionItems.forEach((item) => itemObserver.observe(item));
  }

  updateActiveNavItem(activeId) {
    if (!this.stickyNav) return;

    const navItems = this.stickyNav.querySelectorAll('.accordion-sticky-nav__item');
    navItems.forEach((item) => {
      if (item.getAttribute('data-target') === activeId) {
        item.classList.add('active');
        item.setAttribute('aria-current', 'true');
      } else {
        item.classList.remove('active');
        item.removeAttribute('aria-current');
      }
    });
  }

  handleStickyNavClick(targetItem) {
    // Set navigation flag and force sticky nav to top position
    this.isNavigating = true;
    
    if (this.stickyNav) {
      this.stickyNav.classList.remove('accordion-sticky-nav--bottom');
      this.stickyNav.classList.add('accordion-sticky-nav--top');
    }

    // Scroll to item with offset for sticky nav at top + header
    const stickyNavHeight = this.stickyNav?.offsetHeight || 0;
    const headerHeight = document.querySelector('.section-header')?.offsetHeight || 0;
    const totalOffset = stickyNavHeight + headerHeight + 20;
    
    const elementPosition = targetItem.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - totalOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Open the accordion item and reset navigation flag
    setTimeout(() => {
      if (!targetItem.hasAttribute('open')) {
        targetItem.setAttribute('open', '');
        targetItem.dispatchEvent(new CustomEvent('accordion-toggle', { bubbles: true }));
      }
      targetItem.querySelector('.accordion-header')?.focus();
      
      // Reset navigation flag after scroll completes
      setTimeout(() => {
        this.isNavigating = false;
      }, 100);
    }, 500);
  }

  handleStickyNavKeydown(event, currentIndex, totalItems) {
    const navItems = this.stickyNav.querySelectorAll('.accordion-sticky-nav__item');
    
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        navItems[(currentIndex - 1 + totalItems) % totalItems].focus();
        break;
      case 'ArrowRight':
        event.preventDefault();
        navItems[(currentIndex + 1) % totalItems].focus();
        break;
      case 'Home':
        event.preventDefault();
        navItems[0].focus();
        break;
      case 'End':
        event.preventDefault();
        navItems[totalItems - 1].focus();
        break;
    }
  }

  handleKeydown(event) {
    const headers = this.querySelectorAll('[slot="header"]');
    const currentIndex = Array.from(headers).indexOf(event.target);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        headers[(currentIndex + 1) % headers.length].focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        headers[(currentIndex - 1 + headers.length) % headers.length].focus();
        break;
    }
  }

  handleToggle(event) {
    const allowMultiple = this.hasAttribute('data-allow-multiple');
    const activeItem = event.target;

    if (!allowMultiple) {
      this.querySelectorAll('accordion-item').forEach((item) => {
        if (item !== activeItem) {
          item.removeAttribute('open');
        }
      });
    }
  }

  handleResize() {
    const firstItem = this.querySelector('accordion-item');
    if (!firstItem) return;
    
    // if (this.hasAttribute('data-toggle-first')) {
    //   if (this.isDesktop()) {
    //     firstItem.setAttribute('open', '');
    //   } else {
    //     firstItem.removeAttribute('open');
    //   }
    // }

    if (this.hasAttribute('data-toggle-first')) {
      const firstItem = this.querySelector('accordion-item');
      if (firstItem) {
        firstItem.setAttribute('open', '');
      }
    }

    // Reinitialize or remove sticky nav based on viewport
    if (this.closest('.product-extended-details')) {
      if (!this.stickyNav) {
        this.initStickyNav();
      }
    }
  }

  isDesktop() {
    return window.innerWidth >= 1200;
  }

  setupHeaderHeightTracking() {
    // Update sticky nav position when header height changes
    const updateStickyNavPosition = () => {
      if (this.stickyNav && this.stickyNav.classList.contains('accordion-sticky-nav--top')) {
        const headerHeight = document.querySelector('.section-header')?.offsetHeight || 0;
        this.stickyNav.style.top = `${headerHeight}px`;
      } else if (this.stickyNav && this.stickyNav.classList.contains('accordion-sticky-nav--bottom')) {
        // Clear inline top style when at bottom
        this.stickyNav.style.top = '';
      }
    };

    // Update on scroll
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateStickyNavPosition();
          ticking = false;
        });
        ticking = true;
      }
    });

    // Initial update
    updateStickyNavPosition();
  }
}

// Accordion Item
class AccordionItem extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  connectedCallback() {
    this.header = this.querySelector('[slot="header"]');
    this.content = this.querySelector('.accordion-content');

    // Initialize
    this.setupStyles();
    this.header.setAttribute('tabindex', '0');
    this.header.setAttribute('role', 'button');
    this.header.setAttribute('aria-expanded', this.hasAttribute('open'));
    this.content.style.maxHeight = this.hasAttribute('open') ? `${this.content.scrollHeight}px` : '0px';

    // Event Listeners
    this.header.addEventListener('click', () => this.toggle());
    this.header.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggle();
      }
    });
  }

  attributeChangedCallback(name) {
    if (name === 'open') {
      this.updateState();
    }
  }

  toggle() {
    const isOpen = this.hasAttribute('open');
    if (isOpen) {
      this.removeAttribute('open');
    } else {
      this.setAttribute('open', '');
      this.dispatchEvent(new CustomEvent('accordion-toggle', { bubbles: true }));
    }
  }

  updateState() {
    const isOpen = this.hasAttribute('open');

    if (!this.header || !this.content) {
      this.header = this.querySelector('[slot="header"]');
      this.content = this.querySelector('.accordion-content');
      if (!this.header || !this.content) return;
    }

    this.header.setAttribute('aria-expanded', isOpen);

    if (isOpen) {
      this.content.style.maxHeight = `${this.content.scrollHeight}px`;
    } else {
      this.content.style.maxHeight = '0px';
    }
  }

  setupStyles() {
    // Inline styles for simplicity
    this.content.style.overflow = 'hidden';
    this.content.style.transition = 'max-height 0.3s ease';
    this.content.style.maxHeight = '0px';
  }
}

customElements.define('accordion-element', AccordionElement);
customElements.define('accordion-item', AccordionItem);
